// The request body the Copilot transport sends, with the history stripped of
// its attachments (SPEC-COPILOT-02, D-B2).
//
// The AI SDK keeps every part of every message and re-posts the whole thread on
// each turn. Left alone, the CNH photo of turn one would travel again on turns
// two, three and four — the same bytes billed again, a 20 MB cap reached by the
// third message, and a document sitting in a payload long after the clinic
// stopped talking about it. Media belongs to the turn that carried it: the last
// user message keeps its file parts, every earlier message keeps only a text
// stub naming the document the model already read.

import type { UIMessage } from "ai";

import type { PageContextRequestV1 } from "@/generated/copilot-contracts";

type FilePartLike = { type: string; filename?: string; mediaType?: string };

export function historyStub(filename: string): string {
  return `[Documento enviado: ${filename || "arquivo"} — dados já extraídos]`;
}

function isFilePart(part: unknown): part is FilePartLike {
  return (
    typeof part === "object" &&
    part !== null &&
    (part as { type?: unknown }).type === "file"
  );
}

function withoutFileParts(message: UIMessage): UIMessage {
  const parts = message.parts ?? [];
  if (!parts.some(isFilePart)) return message;

  return {
    ...message,
    parts: parts.map((part) =>
      isFilePart(part)
        ? { type: "text" as const, text: historyStub(part.filename ?? "") }
        : part,
    ),
  } as UIMessage;
}

/** Keeps the file parts of the last user message; replaces every other file
 * part with its text stub. */
export function stripHistoricalFileParts(messages: readonly UIMessage[]): UIMessage[] {
  let lastUserIndex = -1;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].role === "user") {
      lastUserIndex = index;
      break;
    }
  }

  return messages.map((message, index) =>
    index === lastUserIndex ? message : withoutFileParts(message),
  );
}

type PrepareOptions = {
  id: string;
  messages: UIMessage[];
  body?: Record<string, unknown>;
  trigger: "submit-message" | "regenerate-message";
  messageId: string | undefined;
  /** The page-context hint for the screen the turn was sent from (SCRUM-306).
   * `null` on a screen that publishes none. */
  pageContext?: PageContextRequestV1 | null;
};

/** Mirrors the transport's default body, with the stripped history. */
export function prepareCopilotSendRequest(options: PrepareOptions): { body: object } {
  return {
    body: {
      ...options.body,
      id: options.id,
      messages: stripHistoricalFileParts(options.messages),
      trigger: options.trigger,
      messageId: options.messageId,
      // Only when there is one: a `page_context: null` key would make the
      // runtime's request model decide what a null hint means.
      ...(options.pageContext ? { page_context: options.pageContext } : {}),
    },
  };
}
